import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Search, Loader2, ExternalLink, Sparkles, AlertCircle,
  CheckCircle2, Clock, XCircle, RefreshCw, FileText, Image as ImageIcon,
  ChevronDown, Cpu, GitCompare,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';


const STORY_STATUS_BADGES = {
  pending:        { colour: 'bg-slate-100 text-slate-600 border-slate-300',           label: 'Not generated',  icon: Clock },
  in_progress:    { colour: 'bg-blue-50 text-blue-600 border-blue-200',               label: 'Generating',     icon: Loader2 },
  complete:       { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',      label: 'Story created',  icon: CheckCircle2 },
  failed:         { colour: 'bg-red-50 text-red-600 border-red-200',                  label: 'Failed',         icon: XCircle },
  rewrite_failed: { colour: 'bg-amber-50 text-amber-700 border-amber-200',            label: 'Rewrite failed', icon: AlertCircle },
};

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};


function StoryStatusBadge({ status }) {
  const cfg = STORY_STATUS_BADGES[status] || STORY_STATUS_BADGES.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${cfg.colour}`}>
      <Icon size={10} className={status === 'in_progress' ? 'animate-spin' : ''} />
      {cfg.label}
    </span>
  );
}


function ItemRow({ item, models, onGenerate, onView }) {
  const [busy, setBusy]         = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef                 = useRef(null);
  const canGenerate = ['pending', 'rewrite_failed', 'failed'].includes(item.story_creation_status);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const fire = async (modelIds) => {
    setBusy(true);
    setMenuOpen(false);
    try { await onGenerate(item, modelIds); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors shadow-sm">
      <div className="flex items-start justify-between gap-4">
        {item.source_image_url && (
          <img
            src={item.source_image_url}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="w-20 h-14 object-cover rounded shrink-0 bg-slate-100"
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-1">
            <h3 className="text-slate-900 font-medium leading-snug flex-1">{item.title}</h3>
            <StoryStatusBadge status={item.story_creation_status} />
          </div>

          {item.summary && (
            <p className="text-xs text-slate-500 line-clamp-2 mt-1">{item.summary}</p>
          )}

          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400 flex-wrap">
            <span className="text-slate-500">{item.feed_source_name}</span>
            {item.primary_organisation_name && (
              <>
                <span className="text-slate-300">•</span>
                <span>{item.primary_organisation_name}</span>
              </>
            )}
            {item.source_published_at && (
              <>
                <span className="text-slate-300">•</span>
                <span>{fmtUK(item.source_published_at)}</span>
              </>
            )}
            {item.output_count > 0 && (
              <>
                <span className="text-slate-300">•</span>
                <span className="text-emerald-600 inline-flex items-center gap-1">
                  <FileText size={11} /> {item.output_count} {item.output_count === 1 ? 'story' : 'stories'}
                </span>
              </>
            )}
            {!item.source_image_url && (
              <>
                <span className="text-slate-300">•</span>
                <span className="text-amber-500 inline-flex items-center gap-1">
                  <ImageIcon size={11} /> No image
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
            title="Open source"
          >
            <ExternalLink size={14} />
          </a>
          <button
            onClick={() => onView(item)}
            className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
          >
            Details
          </button>
          {canGenerate && (
            <div className="relative flex items-stretch" ref={menuRef}>
              <button
                onClick={() => fire(null)}
                disabled={busy}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white pl-3 pr-2.5 py-1.5 rounded-l text-xs font-medium transition-colors border-r border-blue-500/40"
                title="Generate with default model"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                Generate
              </button>
              <button
                onClick={() => setMenuOpen(o => !o)}
                disabled={busy || models.length === 0}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-1.5 py-1.5 rounded-r text-xs transition-colors flex items-center"
                title="Choose model"
              >
                <ChevronDown size={12} className={menuOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
              {menuOpen && models.length > 0 && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[200px]">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 px-3 py-1.5 font-semibold">Generate with</p>
                  {models.map(m => (
                    <button
                      key={m.id}
                      onClick={() => fire([m.id])}
                      className="w-full text-left px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2"
                    >
                      <Cpu size={11} className="text-slate-400" />
                      {m.label}
                    </button>
                  ))}
                  {models.length >= 2 && (
                    <>
                      <div className="border-t border-slate-200 my-1" />
                      <button
                        onClick={() => fire(models.map(m => m.id))}
                        className="w-full text-left px-3 py-2 text-xs text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                      >
                        <GitCompare size={11} />
                        Compare all models
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function FeedsItemsPage() {
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const [params]     = useSearchParams();
  const sourceFilter = params.get('source_id');

  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [offset, setOffset]     = useState(0);
  const [models, setModels]     = useState([]);
  const limit = 50;

  useEffect(() => { setOffset(0); }, [search, statusFilter, sourceFilter]);
  useEffect(() => { load(); }, [search, statusFilter, sourceFilter, offset]);
  useEffect(() => { loadModels(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const queryParams = { limit, offset };
      if (search)       queryParams.search = search;
      if (sourceFilter) queryParams.source_id = sourceFilter;
      if (statusFilter !== 'all') queryParams.story_status = statusFilter;

      const { data } = await api.get('/feeds/items', { params: queryParams });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    try {
      const { data } = await api.get('/feeds/models');
      setModels(data.models || []);
    } catch {
      // silent — Generate still works with backend default
    }
  };

  const handleGenerate = async (item, modelIds = null) => {
    try {
      const payload  = modelIds && modelIds.length > 0 ? { model_ids: modelIds } : {};
      const { data } = await api.post(`/feeds/items/${item.id}/generate-stories`, payload);
      const created    = data.stories_created || 0;
      const failed     = data.failed || 0;
      const modelsUsed = (data.models_used || []).length;

      if (created > 0 && failed === 0) {
        const tag = modelsUsed > 1 ? ` across ${modelsUsed} models` : '';
        toast.success(`Generated ${created} ${created === 1 ? 'story' : 'stories'}${tag}`);
      } else if (created > 0 && failed > 0) {
        toast.warning(`Generated ${created}, failed ${failed} — check details`);
      } else if (failed > 0) {
        const firstError = data.results?.find(r => !r.success)?.error || 'Generation failed';
        toast.error(`Failed: ${firstError}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    }
  };

  const handleView = (item) => {
    navigate(`/feeds/items/${item.id}`);
  };

  if (user?.role !== 'admin' && user?.role !== 'editor') {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">You do not have access to EventFlow.</p>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate('/feeds')}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm mb-4 transition-colors"
      >
        <ArrowLeft size={14} /> Back to EventFlow
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Feeds</h1>
        <p className="text-slate-500 text-sm mt-1">
          Fetched feed across all sources. Click Generate to rewrite into a publication-ready story.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title or summary..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
          {[
            { id: 'all',            label: 'All' },
            { id: 'pending',        label: 'Not generated' },
            { id: 'complete',       label: 'Stories created' },
            { id: 'rewrite_failed', label: 'Failed' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                statusFilter === t.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <button
          onClick={load}
          className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
          title="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading feed...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          {search || statusFilter !== 'all'
            ? 'No items match your filters.'
            : 'No items yet. Fetch a source to ingest items.'}
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3">
            Showing {offset + 1}–{Math.min(offset + items.length, total)} of {total} · times in UK
          </p>

          <div className="space-y-2">
            {items.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                models={models}
                onGenerate={handleGenerate}
                onView={handleView}
              />
            ))}
          </div>

          {total > limit && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200 transition-colors"
              >
                Previous
              </button>
              <span className="text-xs text-slate-400">
                Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}